import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import {
  SubmitStringGroupTranslationMutationVariables,
  useListStringsLazyQuery,
  useSubmitStringGroupTranslationMutation,
} from 'src/generated/graphql';
import { MAX_SNAPSHOT_PAGES, SNAPSHOT_PAGE_SIZE } from '../constants';
import {
  computeRollbackDelta,
  DuplicateStringIdError,
  isDeltaEmpty,
  VersionString,
} from '../utils/rollbackDelta';

interface RollbackModalProps {
  visible: boolean;
  version: number;
  currentVersion: number;
  namespaceId: string;
  stringGroupId: string;
  cxIds: string[];
  isCurrentVersionCancelled: boolean;
  onDismiss: () => void;
  onSuccess: (newVersion: number) => void;
}

/**
 * Fetches all paginated strings for a given version using the provided lazy
 * query function. Each invocation exhausts pagination before returning.
 *
 * Guards against a backend that never terminates pagination — a repeated
 * nextToken or an unbounded page count fails fast instead of hanging the
 * rollback (the same safeguards ViewVersionModal applies to its snapshot walk).
 */
async function fetchAllPages(
  fetchFn: ReturnType<typeof useListStringsLazyQuery>[0],
  namespaceId: string,
  stringGroupId: string,
  targetVersion: number,
): Promise<VersionString[]> {
  const allStrings: VersionString[] = [];
  const seenPageTokens = new Set<string>();
  let pageCount = 0;
  let nextToken: string | null = null;

  do {
    pageCount += 1;
    if (pageCount > MAX_SNAPSHOT_PAGES) {
      throw new Error('The version snapshot exceeded the pagination limit.');
    }

    const { data, error: fetchError } = await fetchFn({
      fetchPolicy: 'network-only',
      variables: {
        namespaceId,
        stringGroupId,
        stringGroupVersion: targetVersion.toString(),
        // Pinned so MAX_SNAPSHOT_PAGES bounds the same number of strings here
        // as it does in ViewVersionModal's walk.
        maxResults: SNAPSHOT_PAGE_SIZE,
        ...(nextToken ? { nextToken } : {}),
      },
    });

    if (fetchError) {
      throw fetchError;
    }

    // A page that resolves without data must fail loudly. Skipping it would
    // yield an empty (or partial) target snapshot, and computeRollbackDelta
    // would then read that as "every string was deleted" and submit a
    // destructive rollback.
    if (!data) {
      throw new Error('The snapshot response did not contain data.');
    }

    if (data.listStrings.strings) {
      const mapped: VersionString[] = data.listStrings.strings.map((s) => ({
        stringId: s.stringId,
        sourceLocale: s.sourceLocale,
        sourceText: s.sourceText,
        contextNote: s.contextNote,
        inlineMarkupType: s.inlineMarkupType,
        messageFormatType: s.messageFormatType,
        variants: s.variants.map((v) => ({
          cxId: v.cxId,
          sourceText: v.sourceText,
          contextNote: v.contextNote,
        })),
      }));
      allStrings.push(...mapped);
    }

    const returnedNextToken = data.listStrings.nextToken ?? null;
    // Check membership before adding — Set.add() returns the Set (always
    // truthy), so it cannot be used as the "already seen" signal.
    if (returnedNextToken && seenPageTokens.has(returnedNextToken)) {
      throw new Error('The version snapshot returned a repeated page token.');
    }
    if (returnedNextToken) {
      seenPageTokens.add(returnedNextToken);
    }
    nextToken = returnedNextToken;
  } while (nextToken);

  return allStrings;
}

const RollbackModal: React.FC<RollbackModalProps> = ({
  visible,
  version,
  currentVersion,
  namespaceId,
  stringGroupId,
  cxIds,
  isCurrentVersionCancelled,
  onDismiss,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newVersion, setNewVersion] = useState<number | null>(null);
  const [noOpDetected, setNoOpDetected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The parent keeps this modal mounted and only toggles `visible`, so a fresh
  // open must not inherit the previous run's outcome. Without this, a submit
  // that resolves after the user dismissed would leave `showSuccess` set and
  // the next open would render the stale "Rollback Successful" screen.
  useEffect(() => {
    if (!visible) return;
    setLoading(false);
    setShowSuccess(false);
    setNewVersion(null);
    setNoOpDetected(false);
    setError(null);
  }, [visible]);

  // Two separate hooks so parallel fetches don't share an ObservableQuery
  const [fetchCurrentStrings] = useListStringsLazyQuery();
  const [fetchTargetStrings] = useListStringsLazyQuery();
  const [submitMutation] = useSubmitStringGroupTranslationMutation();

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoOpDetected(false);

    try {
      // Fetch both snapshots in parallel using independent query hooks
      const [currentStrings, targetStrings] = await Promise.all([
        fetchAllPages(
          fetchCurrentStrings,
          namespaceId,
          stringGroupId,
          currentVersion,
        ),
        fetchAllPages(fetchTargetStrings, namespaceId, stringGroupId, version),
      ]);

      // Compute the real delta
      const delta = computeRollbackDelta(
        currentStrings,
        targetStrings,
        currentVersion,
        version,
      );

      // If snapshots are identical, show non-destructive message and bail
      if (isDeltaEmpty(delta)) {
        setLoading(false);
        setNoOpDetected(true);
        return;
      }

      // Submit only non-empty arrays
      const variables: SubmitStringGroupTranslationMutationVariables = {
        namespaceId,
        stringGroupId,
        cxIds,
        ...(delta.added.length > 0 ? { added: delta.added } : {}),
        ...(delta.edited.length > 0 ? { edited: delta.edited } : {}),
        ...(delta.deleted.length > 0 ? { deleted: delta.deleted } : {}),
      };

      const { data: mutationData } = await submitMutation({ variables });

      const returnedVersion =
        mutationData?.submitStringGroupTranslation.version || version;

      setNewVersion(returnedVersion);
      setLoading(false);
      setShowSuccess(true);
    } catch (err) {
      setLoading(false);
      if (err instanceof DuplicateStringIdError) {
        setError(`Cannot rollback: ${err.message}. Please contact support.`);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to rollback. Please try again.',
        );
      }
    }
  }, [
    namespaceId,
    stringGroupId,
    version,
    currentVersion,
    cxIds,
    fetchCurrentStrings,
    fetchTargetStrings,
    submitMutation,
  ]);

  const handleSuccessDismiss = useCallback(() => {
    setShowSuccess(false);
    setNewVersion(null);
    onSuccess(newVersion ?? version);
  }, [newVersion, version, onSuccess]);

  const handleDismiss = useCallback(() => {
    // The footer Cancel button is disabled while the rollback is in flight, but
    // Cloudscape still dismisses on ESC/backdrop. Ignoring those keeps the
    // modal open until the submit resolves, so a rollback that already
    // succeeded on the backend always reaches onSuccess (and the parent's
    // refetch/flashbar) instead of completing invisibly.
    if (loading) {
      return;
    }
    setShowSuccess(false);
    setLoading(false);
    setError(null);
    setNoOpDetected(false);
    setNewVersion(null);
    onDismiss();
  }, [loading, onDismiss]);

  if (showSuccess) {
    return (
      <Modal
        visible={visible}
        onDismiss={handleSuccessDismiss}
        header="Rollback Successful"
        footer={
          <Box float="right">
            <Button variant="primary" onClick={handleSuccessDismiss}>
              OK
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="success">
            Rollback to v{version} content has been submitted for translation as
            v{newVersion}.
          </Alert>
          <Box>
            Translation will begin automatically for configured locales.
          </Box>
        </SpaceBetween>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={`Rollback to Version v${version}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={loading}>
              Cancel
            </Button>
            {!noOpDetected && (
              <Button
                variant="primary"
                onClick={handleConfirm}
                loading={loading}
              >
                Confirm
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {noOpDetected ? (
          <Alert type="info">
            Version v{version} is identical to the current version v
            {currentVersion}. No rollback is needed.
          </Alert>
        ) : isCurrentVersionCancelled ? (
          <Alert type="warning">
            This string group was cancelled before all translations completed.
            Some translations may be missing due to cancellation.
          </Alert>
        ) : (
          <Alert type="warning">
            Rollback to this version will submit the v{version} content as a new
            version. All strings will be compared against the current v
            {currentVersion} and only differences will be applied.
          </Alert>
        )}
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
};

export default RollbackModal;
